import { dispatchEditNotifications } from './tradeNotifications';

// Runs the Trade Recommendation submit flow: format payload, call onSubmit,
// derive edit-path triggers, then dispatch notifications.
// Body moved verbatim from TradeRecommendationForm.onFormSubmit.
export const runTradeFormSubmit = async ({
  data,                    // raw field values from the form
  onSubmit,                // save function supplied by the page (calls the API)
  isEditing,               // true when updating an existing trade
  tradeRecommendation,     // the trade being edited (if any)
  setLoading,              // toggles the saving spinner
  setValue,                // sets a field value from code (auto-fills)
  initialStopLoss,         // original values (for change detection)
  initialTargetOne,
  initialExitType,
  initialTargetOneStatus,
  fetchduration,           // maps a trade-type id to its label (for one message)
}) => {
  if (onSubmit) {
      // Convert text inputs to numbers for the backend; blank/NaN falls back to 0.
      const formattedData = {
        ...data,

        recoPriceLow: parseFloat(data.recoPriceLow) || 0,
        recoPriceHigh: parseFloat(data.recoPriceHigh) || 0,
        targetOne: parseFloat(data.targetOne) || 0,
        targetTwo: parseFloat(data.targetTwo) || 0,
        targetThree: parseFloat(data.targetThree) || 0,
        stopLoss: parseFloat(data.stopLoss) || 0,
        track_only: !!data.track_only,
        ...(isEditing && {
          tradeRecommendationId: tradeRecommendation?.traderecommendationid || tradeRecommendation?.tradeRecommendationId
        }),
      };

      // Flags describing what changed; the notification step reads these.
      let submitSucceeded = false;
      let stopLossChanged = false;
      let targetUpdated = false;
      let manualExitTriggered = false;
      let profitPercent = 0;
      let script = "";
      let targetHitTriggered = false;
      let stopLossHitTriggered = false;
      let duration = "";
      let scriptTypeId = 0;      // 1=Equity, 2=Futures, 3=Options
      let side = 0;              // 1=Buy, 2=Sell (decides profit direction)
      let actualProfit = 0;
      let isClosed;
      let actualProfitpercentage = 0;


      try {
        setLoading(true);
        const response = await onSubmit(formattedData);   // the actual save

        // Save worked — now compare original vs new to set the change flags.
        submitSucceeded = true;
        stopLossChanged =
          isEditing &&
          initialStopLoss !== null &&
          Number(initialStopLoss) !== Number(formattedData.stopLoss);
        targetUpdated =
          isEditing &&
          initialTargetOne !== null &&
          Number(initialTargetOne) !== Number(formattedData.targetOne);


        isClosed =
          String(formattedData.status || "").toLowerCase() === "closed";

        // Closing without an exit type — default it to Manual (1).
        if (isClosed && !formattedData.exitTypeId) {
          setValue("exitTypeId", "1", { shouldDirty: true, shouldValidate: true });
        }
        manualExitTriggered =
          isEditing &&
          isClosed &&
          String(initialExitType || "") !== "1";



        // profit % calculate
        if(formattedData.tradeRecommendationId == 1) {
          profitPercent = formattedData.recoPriceLow
          ? (
            ((formattedData.targetOne - formattedData.recoPriceLow) /
              formattedData.recoPriceLow) *
            100
          ).toFixed(2)
          : 0;
        } else if(formattedData.tradeRecommendationId == 2) {
             profitPercent = formattedData.recoPriceLow
          ? (
            ((formattedData.recoPriceLow - formattedData.targetOne) /
              formattedData.recoPriceLow) *
            100
          ).toFixed(2)
          : 0;
        }
        script = formattedData.script;

        targetHitTriggered =
          isEditing &&
          String(formattedData.status || "").toLowerCase() === "target hit" &&
          String(initialTargetOneStatus || "").toLowerCase() !== "target hit";

        // Keep the per-target status in sync with the overall status.
        if (formattedData.status === "Target Hit" && !formattedData.targetOneStatus) {
          setValue("targetOneStatus", "Target Hit", { shouldDirty: true });
        }

        if (formattedData.status === "Stoploss Hit" && !formattedData.targetThreeStatus) {
          setValue("targetThreeStatus", "Stoploss Hit", { shouldDirty: true });
        }

        // Safer stoploss trigger based on status

        stopLossHitTriggered =
          isEditing &&
          (String(formattedData.status || "").toLowerCase() === "stoploss hit" ||
            String(formattedData.status || "").toLowerCase() === "stop loss hit") &&
          String(initialTargetOneStatus || "").toLowerCase() !== "stoploss hit";


        duration =
          formattedData.recoDate && formattedData.exitDate
            ? `${Math.ceil(
              (new Date(formattedData.exitDate) -
                new Date(formattedData.recoDate)) /
              (1000 * 60 * 60 * 24),
            )} Days`
            : "";

        scriptTypeId = Number(formattedData.scriptTypeId);
        side = Number(formattedData.tradeRecommendationId);
      } catch (error) {
        console.error("Submit failed", error);
      } finally {
        // Profit direction differs for Buy (side 1) vs Sell (side 2).
        if (side === 1) {
          actualProfit = Number(formattedData.targetOne) - Number(formattedData.recoPriceLow);
          actualProfitpercentage =
            ((Number(formattedData.targetOne) - Number(formattedData.recoPriceLow)) /
              Number(formattedData.recoPriceLow)) *
            100;
        } else if (side === 2) {
          actualProfit = Number(formattedData.recoPriceLow) - Number(formattedData.targetOne);
          actualProfitpercentage =
            ((Number(formattedData.recoPriceLow) - Number(formattedData.targetOne)) /
              Number(formattedData.recoPriceLow)) *
            100;
        }

        setLoading(false);
      }

      // Only notify when the save succeeded and the trade isn't "track only".
      if (submitSucceeded && !formattedData.track_only) {
        dispatchEditNotifications({
          formattedData,
          scriptTypeId,
          stopLossChanged,
          targetUpdated,
          targetHitTriggered,
          stopLossHitTriggered,
          manualExitTriggered,
          initialStopLoss,
          initialTargetOne,
          actualProfit,
          actualProfitpercentage,
          fetchduration,
          setLoading,
        });
      }
  }
};
